import { motion } from 'framer-motion';
import { ArrowRight, Linkedin, Youtube, Facebook, Instagram } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GGJLogoMark } from '@shared/components/Brand/GGJLogo';
import { landingPrimaryCtaLgOnBrand, landingSectionY } from './landingCtaStyles';

/**
 * Base path (no extension) for the CTA illustration.
 * Drop any of .webp / .png / .jpg / .svg into public/images/landing/
 * with the filename `cta-illustration` — the component tries all formats.
 */
const CTA_ILLUSTRATION_BASE = '/images/landing/cta-illustration';
const CTA_IMG_EXTS = ['.webp', '.png', '.jpg', '.jpeg', '.svg'] as const;

function CtaIllustration() {
  const [extIdx, setExtIdx] = useState(0);
  useEffect(() => { setExtIdx(0); }, []);

  const allExhausted = extIdx >= CTA_IMG_EXTS.length;
  const src = allExhausted ? '' : `${CTA_ILLUSTRATION_BASE}${CTA_IMG_EXTS[extIdx]}`;

  if (!allExhausted) {
    return (
      <div className="relative h-full w-full overflow-hidden rounded-r-2xl md:rounded-r-3xl">
        <img
          key={src}
          src={src}
          alt="GoGrabJob CTA illustration"
          className="h-full w-full object-cover object-center"
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setExtIdx((i) => i + 1)}
        />
        {/* Covers watermark badge in the bottom-left corner */}
        <div className="pointer-events-none absolute bottom-0 left-0 h-8 w-16 bg-[#8E86FF]" aria-hidden />
      </div>
    );
  }

  return (
    <div className="flex min-h-[200px] w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-white/30 bg-white/10 px-4 py-8 text-center">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-white/60">
        Add illustration
      </span>
      <span className="break-all font-mono text-[10px] leading-snug text-white/50">
        {CTA_ILLUSTRATION_BASE}.(webp|png|jpg|svg)
      </span>
    </div>
  );
}

/** Final CTA slab — brand primary surface `#8E86FF` (B2C `light.html` --primary) */
const FINAL_CTA_PANEL_CLASS =
  'mx-auto w-full max-w-[1600px] overflow-hidden rounded-2xl border border-[#7B6EE8] bg-[#8E86FF] shadow-[0_8px_32px_rgba(0,0,0,0.12)] md:rounded-3xl dark:border-[#7B6EE8] dark:bg-[#8E86FF] dark:shadow-black/35';

/** Same horizontal inset as CTA section — keeps CTA + footer cards the same width on all breakpoints */
const LANDING_WIDE_BAND_GUTTER_X = 'px-2 sm:px-4';

export function B2BFinalCTA() {
  return (
    <section id="cta" className={`scroll-mt-28 ${LANDING_WIDE_BAND_GUTTER_X} ${landingSectionY}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className={FINAL_CTA_PANEL_CLASS}
      >
        <div className="flex flex-col md:flex-row md:items-stretch">
          {/* Illustration — left 50%, flush to card edges */}
          <div className="order-2 min-h-[160px] w-full p-0 md:order-1 md:w-[50%]">
            <CtaIllustration />
          </div>

          {/* Text + CTA — right half, right-aligned on desktop */}
          <div className="order-1 flex min-w-0 flex-1 flex-col justify-center px-6 py-4 text-center md:order-2 md:px-8 md:py-5 md:text-right lg:px-10 lg:py-6">
            <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">
              AI-powered hiring, end to end
            </p>
            <h2 className="mb-2.5 text-2xl font-extrabold leading-tight tracking-tight text-white sm:text-3xl md:text-[2rem] lg:text-[2.2rem] lg:leading-[1.15]">
              Screen 10× faster.
              <br />
              Hire with confidence.
            </h2>
            <p className="mb-3 ml-auto max-w-lg text-sm font-normal leading-relaxed text-white/85 sm:text-base">
              Post roles, score candidates with explainable fit, run multilingual AI video &amp; voice interviews, and consolidate evidence — all in one calm workspace built for speed.
            </p>

            {/* Trust badges */}
            <div className="mb-3 flex flex-wrap justify-center gap-2 md:justify-end">
              {['No credit card', '14-day trial', 'GDPR-ready'].map((badge) => (
                <span
                  key={badge}
                  className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white/80"
                >
                  ✓ {badge}
                </span>
              ))}
            </div>

            <div className="flex flex-col flex-wrap justify-center gap-3 sm:flex-row md:justify-end">
              <Link to="/business/demo" className={landingPrimaryCtaLgOnBrand}>
                See it live — request a demo
                <ArrowRight className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" aria-hidden />
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 *  B2BFooter — Dripify-style footer
 *  Layout: Logo+tagline (left) · PRODUCT+PARTNERS · SOFTWARE · RESOURCES · LEGAL · COMPARE
 *  Bottom bar: Social icons (left) · Copyright (right)
 * ───────────────────────────────────────────────────────────────────────── */

const COL_HEAD = 'text-[11px] sm:text-xs font-semibold tracking-[0.12em] text-[#8b93a3] uppercase mb-2 mt-0';
const FOOT_LINK = 'min-h-[34px] flex w-full items-center rounded-md py-1 text-left text-sm text-[#b4bac8] transition-colors hover:text-white hover:bg-white/[0.06] touch-manipulation';

function FTab({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <li>
      <a href={href} target="_blank" rel="noopener noreferrer" className={FOOT_LINK}>
        {children}
      </a>
    </li>
  );
}

export function B2BFooter() {
  return (
    <footer className={`w-full bg-transparent pb-3 pt-1 sm:pb-4 md:pb-5 lg:pb-6 ${LANDING_WIDE_BAND_GUTTER_X}`}>
      <div className="b2b-marketing-footer mx-auto w-full max-w-[1600px] overflow-hidden rounded-[1.75rem] bg-[#2D333F] px-6 text-[#e8eaef] shadow-[0_12px_40px_-8px_rgba(0,0,0,0.22)] sm:rounded-[2rem] md:rounded-[2.25rem] md:px-10 lg:rounded-[2.5rem] lg:px-14 pt-10 sm:pt-12 md:pt-14 transition-colors duration-300 pb-[calc(2rem+env(safe-area-inset-bottom))] sm:pb-[calc(2.5rem+env(safe-area-inset-bottom))] lg:pb-[calc(3rem+env(safe-area-inset-bottom))]">
        <div className="mx-auto min-w-0 w-full overflow-x-hidden">

          {/* ── Main grid: logo col + 5 link columns (Dripify layout) ── */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-[200px_1fr_1fr_1fr_1fr_1fr] lg:gap-x-10 mb-10 sm:mb-12">

            {/* Logo + tagline — leftmost */}
            <div className="col-span-2 sm:col-span-1 flex gap-3 items-start">
              <GGJLogoMark size={34} className="shrink-0 mt-0.5" forceDarkSurface />
              <div className="min-w-0">
                <span className="text-base font-bold text-white tracking-tight block leading-tight">GoGrabJob</span>
                <span className="block text-xs font-medium text-violet-300/90 mt-0.5">Business</span>
                <p className="mt-2 text-xs leading-snug text-[#a8b0bf]">
                  Reduce time-to-hire.<br />Improve quality of hire.
                </p>
              </div>
            </div>

            {/* Column 1 — Product + Partners stacked */}
            <div className="min-w-0">
              <h3 className={COL_HEAD}>Product</h3>
              <ul className="space-y-0.5">
                <FTab href="/business/features">Features</FTab>
              </ul>
              <h3 className={`${COL_HEAD} mt-7`}>Partners</h3>
              <ul className="space-y-0.5">
                <FTab href="/business/affiliate">Affiliate Program</FTab>
              </ul>
            </div>

            {/* Column 2 — Software (5 features) */}
            <div className="min-w-0">
              <h3 className={COL_HEAD}>Software</h3>
              <ul className="space-y-0.5">
                <FTab href="/business/software/candidates-pipeline">Candidates &amp; Pipeline</FTab>
                <FTab href="/business/software/ai-interviews">AI Video Interviews</FTab>
                <FTab href="/business/software/role-fit">Explainable Role-Fit</FTab>
                <FTab href="/business/software/workflow-engine">Visual Workflow Engine</FTab>
                <FTab href="/business/software/agency-mode">Agency Client Mode</FTab>
              </ul>
            </div>

            {/* Column 3 — Resources */}
            <div className="min-w-0">
              <h3 className={COL_HEAD}>Resources</h3>
              <ul className="space-y-0.5">
                <FTab href="/business/blog">Blog</FTab>
                <FTab href="/business/faq">FAQ</FTab>
                <FTab href="/business/help-center">Help Center</FTab>
                <FTab href="/business/contact-us">Contact Us</FTab>
              </ul>
            </div>

            {/* Column 4 — Legal */}
            <div className="min-w-0">
              <h3 className={COL_HEAD}>Legal</h3>
              <ul className="space-y-0.5">
                <FTab href="/business/privacy-policy">Privacy Policy</FTab>
                <FTab href="/business/terms-of-service">Terms of Service</FTab>
                <FTab href="/business/cookie-policy">Cookie Policy</FTab>
                <FTab href="/business/gdpr-compliance">GDPR Compliance</FTab>
                <FTab href="/business/trust-center">Trust Center (SOC2)</FTab>
              </ul>
            </div>

            {/* Column 5 — Compare */}
            <div className="min-w-0">
              <h3 className={COL_HEAD}>Compare</h3>
              <ul className="space-y-0.5">
                <FTab href="/business/compare/greenhouse">vs Greenhouse</FTab>
                <FTab href="/business/compare/hirevue">vs HireVue</FTab>
                <FTab href="/business/compare/lever">vs Lever</FTab>
                <FTab href="/business/compare/vincere">vs Vincere</FTab>
                <FTab href="/business/compare/ismartrecruit">vs iSmartRecruit</FTab>
              </ul>
            </div>

          </div>

          {/* ── Bottom bar: social icons left · copyright right ── */}
          <div className="border-t border-[#4a5568]/80 pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

            {/* Social icons */}
            <div className="flex items-center gap-2">
              {[
                { href: 'https://www.linkedin.com/company/gograbjobofficial', label: 'LinkedIn',  Icon: Linkedin  },
                { href: 'https://www.youtube.com/@GoGrabJob',                 label: 'YouTube',   Icon: Youtube   },
                { href: 'https://www.facebook.com/profile.php?id=61579650282244', label: 'Facebook', Icon: Facebook },
                { href: 'https://www.instagram.com/gograbjob_ai/',              label: 'Instagram', Icon: Instagram },
              ].map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#5a6470]/80 text-[#b4bac8] transition-all hover:border-[#7a8494] hover:bg-white/[0.10] hover:text-white"
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>

            {/* Copyright */}
            <span className="text-xs text-[#6b7280]">
              © 2026{' '}
              <a
                href="https://www.zykestech.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9aa3b2] hover:text-white transition-colors"
              >
                Zykes Technologies LLP
              </a>
              {' '}· All rights reserved.
            </span>

          </div>
        </div>
      </div>
    </footer>
  );
}
